/**
 * Copyright (c) Microsoft. All rights reserved.
 */

export type BatchPromisesResults<T> = {
    results: Result<T>[];
    failedCount: number;
};

export type Result<T> = { success?: T; error?: any; status: 'success' | 'error' };

/**
 * Perform a batch promises operation that returns a promise of results,
 * each result has either success or error object that wraps the desired value.
 * Returns e.g. [{success: 'success value'}, {error: 'error message'}].
 * Usage: const {results, failedCount} = await batchPromises([array of promises])
 *
 * @param operations The array of promises to resolve.
 * @returns {results, failedCount} Array of results and the number of the rejected promises.
 */
export const batchPromises = async <T>(operations: Promise<T>[]): Promise<BatchPromisesResults<T>> => {
    const onSuccess = (value: T): Result<T> => ({ success: value, status: 'success' });
    const onReject = (err: any): Result<any> => ({ error: err, status: 'error' });
    const transform = (values: Result<T>[]) => ({
        results: values,
        failedCount: values.filter(val => val.error).length
    });

    return Promise.all(operations.map(op => op.then(onSuccess).catch(onReject))).then(transform);
};

export type NextPageLinkResult = { nextPageLink: string };
export type NextLinkResult = { nextLink: string };
export type NextAbleResults<Key extends string, T> = {
    [k in Key]: T[];
} &
    (NextPageLinkResult | NextLinkResult);

export const getNextAbleResults = async <Key extends string, T>(
    serviceCall: (nextLink: string, args?: any) => Promise<NextAbleResults<Key, T>>,
    key: Key,
    args: any[] = [],
    initialNextPageLink: string = null
): Promise<T[]> => {
    const buffer: T[] = [];
    let nextPageLink: string = initialNextPageLink;

    do {
        const results = await serviceCall(nextPageLink, ...args);
        nextPageLink = (<NextPageLinkResult>results)?.nextPageLink || (<NextLinkResult>results)?.nextLink;
        buffer.push(...results[key]);
    } while (nextPageLink);

    return buffer;
};
